import React, { useState, useEffect } from 'react';
import { Smile, Frown, Heart, TrendingUp, Users, MessageCircle, Loader, X, Send, Star, ExternalLink, LogOut, User } from 'lucide-react';
import { signInWithPopup, signOut, onAuthStateChanged } from 'firebase/auth';
import { auth, googleProvider } from '../../firebase/config';
import { firebaseOperations } from '../../firebase/operations';
import emailjs from '@emailjs/browser';

// EmailJS configuration - replace with your actual values
const SERVICE_ID = 'service_4nze7q9';
const TEMPLATE_ID = 'template_gi1alls';
const PUBLIC_KEY = 'IAPeuK6_oPHoCbqtp';

const PatientFeedback = () => {
  const [reactions, setReactions] = useState({ happy: 0, unhappy: 0, total: 0 });
  const [userReaction, setUserReaction] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showThankYou, setShowThankYou] = useState(false);
  const [animateNumbers, setAnimateNumbers] = useState(false);
  const [error, setError] = useState(null);
  
  // Authentication states
  const [user, setUser] = useState(null);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [pendingReactionType, setPendingReactionType] = useState(null);
  
  // Feedback modal states
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackSuggestion, setFeedbackSuggestion] = useState('');
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  
  // Google Review redirect states
  const [showReviewModal, setShowReviewModal] = useState(false);

  // Helper function to check if user is from Pristine Smile
  const isPristineSmileUser = (userEmail) => {
    if (!userEmail) return false;
    // Add your clinic's email domains here
    const staffEmail = ['reachout@pristinesmile.in', 'harshilpatel4603@gmail.com'];
    return staffEmail.includes(userEmail.toLowerCase());  
  };

  // Load initial reactions and set up auth listener
  useEffect(() => {
    loadReactions();
    
    // Set up auth state listener
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
    });

    return () => unsubscribe();
  }, []);

  const loadReactions = async () => {
    try {
      setIsLoading(true);
      const data = await firebaseOperations.getReactions();
      setReactions(data);
      setError(null);
    } catch (error) {
      console.error('Error loading reactions:', error);
      setError('Failed to load feedback data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setIsAuthenticating(true);
      setError(null);
      
      const result = await signInWithPopup(auth, googleProvider);
      setUser(result.user);
      setShowAuthModal(false);
      
      // Process pending reaction if any
      if (pendingReactionType) {
        await processReaction(pendingReactionType, result.user);
      }
    } catch (error) {
      console.error('Error signing in with Google:', error);
      setError('Failed to sign in with Google. Please try again.');
    } finally {
      setIsAuthenticating(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setUserReaction(null);
      setPendingReactionType(null);
    } catch (error) {
      console.error('Error signing out:', error);
      setError('Failed to sign out');
    }
  };

  const handleReaction = async (type) => {
    if (userReaction || isSubmitting) return;
    
    // Check if user is authenticated
    if (!user) {
      setPendingReactionType(type);
      setShowAuthModal(true);
      return;
    }

    await processReaction(type, user);
  };

  const processReaction = async (type, currentUser) => {
    setIsSubmitting(true);
    setError(null);
    
    try {
      const updatedReactions = await firebaseOperations.addReaction(type, currentUser.uid);
      setReactions(updatedReactions);
      setUserReaction(type);
      setAnimateNumbers(true);
      setPendingReactionType(null);
      
      // Reset animations
      setTimeout(() => setAnimateNumbers(false), 600);
      
      // Handle post-reaction flow
      if (type === 'happy') {
        setShowThankYou(true);
        setTimeout(() => {
          setShowThankYou(false);
          // Only show review modal for non-clinic users
          if (!isPristineSmileUser(currentUser.email)) {
            setShowReviewModal(true);
          }
        }, 2000);
      } else {
        setShowThankYou(true);
        setTimeout(() => {
          setShowThankYou(false);
          // Only show feedback modal for non-clinic users
          if (!isPristineSmileUser(currentUser.email)) {
            setShowFeedbackModal(true);
          }
        }, 2000);
      }
      
    } catch (error) {
      console.error('Error submitting reaction:', error);
      setError('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const sendMail = async (feedbackText) => {
    const currentDate = new Date().toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

    const templateParams = {
      to_name: "Pristine Smile Team",
      from_name: user?.displayName || "Patient",
      practice_name: "Pristine Smile Dental",
      practice_address: "Nilamber corporate park, 226, Vadodara, 390007",
      practice_phone: "+91 84017 49379",
      practice_website: "www.pristinesmile.in",
      to_email: "reachout.pristinesmile@gmail.com",
      patient_name: user?.displayName || "Anonymous Patient",
      patient_email: user?.email || "Not provided",
      feedback_type: "Improvement Suggestion",
      feedback_date: currentDate,
      feedback_message: feedbackText,
      message: `New Patient Feedback Received

Patient Information:
- Name: ${user?.displayName || "Anonymous Patient"}
- Email: ${user?.email || "Not provided"}
- Feedback Date: ${currentDate}
- Feedback Type: Needs Improvement

Feedback Details:
${feedbackText}

This feedback was submitted through the patient feedback system. Please review and take appropriate action to address the patient's concerns.

Best regards,
Pristine Smile Feedback System`
    };

    await emailjs.send(
      SERVICE_ID,
      TEMPLATE_ID,
      templateParams,
      PUBLIC_KEY
    );
  };

  const handleFeedbackSubmit = async () => {
    if (!feedbackSuggestion.trim()) return;
    
    setIsSubmittingFeedback(true);
    try {
      await sendMail(feedbackSuggestion);
      setShowFeedbackModal(false);
      setFeedbackSuggestion('');
      // Show final thank you
      setShowThankYou(true);
      setTimeout(() => setShowThankYou(false), 3000);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setError('Failed to submit feedback suggestions');
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  const openGoogleReview = () => {
    // Replace with your actual Google Business Profile review URL
    const reviewUrl = "https://www.google.com/maps/place/Pristine+Smile/@22.3011357,73.1369405,17z/data=!4m8!3m7!1s0x395fc956227309d5:0x79a69c5171205095!8m2!3d22.3011308!4d73.1395154!9m1!1b1!16s%2Fg%2F11wm_tmsy_?entry=ttu&g_ep=EgoyMDI1MDgyNS4wIKXMDSoASAFQAw%3D%3D";
    window.open(reviewUrl, '_blank');
    setShowReviewModal(false);
  };

  const AuthModal = () => (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4"
         style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
      <div className="rounded-2xl p-8 max-w-md w-full shadow-2xl"
           style={{ backgroundColor: '#ffffff' }}>
        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
               style={{ backgroundColor: '#dbeafe' }}>
            <User className="w-8 h-8" style={{ color: '#2563eb' }} />
          </div>
          <h3 className="text-2xl font-bold mb-2" style={{ color: '#1f2937' }}>
            Sign In Required
          </h3>
          <p style={{ color: '#6b7280' }}>
            Please sign in with your Google account to provide feedback and help us improve our services.
          </p>
        </div>
        
        <button
          onClick={handleGoogleSignIn}
          disabled={isAuthenticating}
          className="w-full rounded-lg px-4 py-3 flex items-center justify-center gap-3 transition-colors mb-4 disabled:opacity-50"
          style={{ 
            backgroundColor: '#ffffff',
            border: '1px solid #d1d5db',
            color: '#374151'
          }}
          onMouseEnter={(e) => !isAuthenticating && (e.target.style.backgroundColor = '#f9fafb')}
          onMouseLeave={(e) => !isAuthenticating && (e.target.style.backgroundColor = '#ffffff')}
        >
          {isAuthenticating ? (
            <Loader className="w-5 h-5 animate-spin" />
          ) : (
            <img 
              src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='%234285F4' d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z'/%3E%3Cpath fill='%2334A853' d='M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z'/%3E%3Cpath fill='%23FBBC05' d='M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z'/%3E%3Cpath fill='%23EA4335' d='M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z'/%3E%3C/svg%3E" 
              alt="Google" 
              className="w-5 h-5" 
            />
          )}
          <span className="font-medium">
            {isAuthenticating ? 'Signing in...' : 'Continue with Google'}
          </span>
        </button>
        
        <button
          onClick={() => {
            setShowAuthModal(false);
            setPendingReactionType(null);
          }}
          className="w-full transition-colors"
          style={{ color: '#6b7280' }}
          onMouseEnter={(e) => e.target.style.color = '#374151'}
          onMouseLeave={(e) => e.target.style.color = '#6b7280'}
        >
          Cancel
        </button>
      </div>
    </div>
  );

  const FeedbackModal = () => (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4"
         style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
      <div className="rounded-2xl p-8 max-w-lg w-full shadow-2xl"
           style={{ backgroundColor: '#ffffff' }}>
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold" style={{ color: '#1f2937' }}>
            Help Us Improve
          </h3>
          <button
            onClick={() => setShowFeedbackModal(false)}
            className="transition-colors"
            style={{ color: '#6b7280' }}
            onMouseEnter={(e) => e.target.style.color = '#374151'}
            onMouseLeave={(e) => e.target.style.color = '#6b7280'}
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <p className="mb-6" style={{ color: '#6b7280' }}>
          We're sorry your experience wasn't perfect. Your feedback is valuable to us - please share what we can do better.
        </p>
        
        <textarea
          value={feedbackSuggestion}
          onChange={(e) => setFeedbackSuggestion(e.target.value)}
          placeholder="Please describe what we can improve..."
          rows={4}
          className="w-full p-4 rounded-lg resize-none mb-6 focus:outline-none focus:ring-2"
          style={{ 
            border: '1px solid #d1d5db',
            focusRingColor: '#2563eb'
          }}
        />
        
        <div className="flex gap-4">
          <button
            onClick={() => setShowFeedbackModal(false)}
            className="flex-1 px-6 py-3 rounded-lg transition-colors"
            style={{ 
              border: '1px solid #d1d5db',
              color: '#374151'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            Skip
          </button>
          <button
            onClick={handleFeedbackSubmit}
            disabled={!feedbackSuggestion.trim() || isSubmittingFeedback}
            className="flex-1 px-6 py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            style={{ 
              backgroundColor: '#2563eb',
              color: '#ffffff'
            }}
            onMouseEnter={(e) => !e.target.disabled && (e.target.style.backgroundColor = '#1d4ed8')}
            onMouseLeave={(e) => !e.target.disabled && (e.target.style.backgroundColor = '#2563eb')}
          >
            {isSubmittingFeedback ? (
              <Loader className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
            Submit
          </button>
        </div>
      </div>
    </div>
  );

  const ReviewModal = () => (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4" 
         style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
      <div className="rounded-2xl p-8 max-w-md w-full shadow-2xl" 
           style={{ backgroundColor: '#ffffff' }}>
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
               style={{ backgroundColor: '#fef3c7' }}>
            <Star className="w-8 h-8" style={{ color: '#f59e0b' }} />
          </div>
          <h3 className="text-2xl font-bold mb-4" style={{ color: '#1f2937' }}>
            Love Your Experience?
          </h3>
          <p className="leading-relaxed" style={{ color: '#6b7280' }}>
            We're thrilled you had a great experience! Would you mind sharing a review on Google to help other patients?
          </p>
        </div>
        
        <div className="space-y-3">
          <button
            onClick={openGoogleReview}
            className="w-full px-6 py-4 rounded-xl transition-colors flex items-center justify-center gap-3 font-semibold text-lg shadow-lg hover:opacity-90"
            style={{ backgroundColor: '#2563eb', color: '#ffffff' }}
          >
            <ExternalLink className="w-5 h-5" />
            Leave Review
          </button>
          <button
            onClick={() => setShowReviewModal(false)}
            className="w-full px-6 py-3 rounded-xl transition-colors font-medium hover:opacity-90"
            style={{ 
              border: '1px solid #d1d5db', 
              color: '#374151', 
              backgroundColor: '#ffffff' 
            }}
          >
            Maybe Later
          </button>
        </div>
      </div>
    </div>
  );

  const happyPercentage = reactions.total > 0 ? Math.round((reactions.happy / reactions.total) * 100) : 0;

  if (isLoading) {
    return (
      <div className="min-h-screen relative overflow-hidden" style={{
        background: 'linear-gradient(135deg, #eff6ff 0%, #ffffff 50%, #f0fdf4 100%)'
      }}>
        {/* Animated background shapes */}
        <div className="absolute inset-0">
          <div 
            className="absolute opacity-30 rounded-full"
            style={{
              top: '5rem',
              left: '2.5rem',
              width: '18rem',
              height: '18rem',
              background: 'linear-gradient(135deg, #dbeafe, #bfdbfe)',
              filter: 'blur(40px)',
              animation: 'float 7s ease-in-out infinite'
            }}
          ></div>
          <div 
            className="absolute opacity-30 rounded-full"
            style={{
              top: '10rem',
              right: '2.5rem',
              width: '18rem',
              height: '18rem',
              background: 'linear-gradient(135deg, #dcfce7, #bbf7d0)',
              filter: 'blur(40px)',
              animation: 'float 7s ease-in-out infinite 2s'
            }}
          ></div>
          <div 
            className="absolute opacity-20 rounded-full"
            style={{
              bottom: '5rem',
              left: '5rem',
              width: '18rem',
              height: '18rem',
              background: 'linear-gradient(135deg, #e0f2fe, #bae6fd)',
              filter: 'blur(40px)',
              animation: 'float 7s ease-in-out infinite 4s'
            }}
          ></div>
          
          {/* Floating icons */}
          <div className="absolute inset-0 opacity-5 pointer-events-none">
            <div className="absolute text-6xl" style={{ top: '8rem', left: '25%' }}>🦷</div>
            <div className="absolute text-4xl" style={{ top: '16rem', right: '25%' }}>✨</div>
            <div className="absolute text-5xl" style={{ bottom: '8rem', left: '33%' }}>😊</div>
            <div className="absolute text-3xl" style={{ top: '50%', right: '33%' }}>💙</div>
            <div className="absolute text-4xl" style={{ bottom: '25%', right: '25%' }}>🦷</div>
          </div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 pt-32 text-center">
          <Loader className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading feedback system...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden" style={{
      background: 'linear-gradient(135deg, #eff6ff 0%, #ffffff 50%, #f0fdf4 100%)'
    }}>
      {/* Animated background shapes */}
      <div className="absolute inset-0">
        <div 
          className="absolute opacity-30 rounded-full"
          style={{
            top: '5rem',
            left: '2.5rem',
            width: '18rem',
            height: '18rem',
            background: 'linear-gradient(135deg, #dbeafe, #bfdbfe)',
            filter: 'blur(40px)',
            animation: 'float 7s ease-in-out infinite'
          }}
        ></div>
        <div 
          className="absolute opacity-30 rounded-full"
          style={{
            top: '10rem',
            right: '2.5rem',
            width: '18rem',
            height: '18rem',
            background: 'linear-gradient(135deg, #dcfce7, #bbf7d0)',
            filter: 'blur(40px)',
            animation: 'float 7s ease-in-out infinite 2s'
          }}
        ></div>
        <div 
          className="absolute opacity-20 rounded-full"
          style={{
            bottom: '5rem',
            left: '5rem',
            width: '18rem',
            height: '18rem',
            background: 'linear-gradient(135deg, #e0f2fe, #bae6fd)',
            filter: 'blur(40px)',
            animation: 'float 7s ease-in-out infinite 4s'
          }}
        ></div>
        
        {/* Floating icons */}
        <div className="absolute inset-0 opacity-5 pointer-events-none">
          <div className="absolute text-6xl" style={{ top: '8rem', left: '25%' }}>🦷</div>
          <div className="absolute text-4xl" style={{ top: '16rem', right: '25%' }}>✨</div>
          <div className="absolute text-5xl" style={{ bottom: '8rem', left: '33%' }}>😊</div>
          <div className="absolute text-3xl" style={{ top: '50%', right: '33%' }}>💙</div>
          <div className="absolute text-4xl" style={{ bottom: '25%', right: '25%' }}>🦷</div>
        </div>
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-4 py-16">
        {/* User Authentication Status */}
        {user && (
          <div className="flex justify-end mb-4">
            <div className="flex items-center gap-4 px-4 py-2 bg-white bg-opacity-90 rounded-full shadow-lg border">
              <div className="flex items-center gap-2">
                {user.photoURL ? (
                  <img src={user.photoURL} alt="Profile" className="w-8 h-8 rounded-full" />
                ) : (
                  <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                    <User className="w-4 h-4 text-white" />
                  </div>
                )}
                <span className="text-sm font-medium text-gray-700">{user.displayName}</span>
                {/* Optional: Add a badge to show if it's a clinic user */}
                {isPristineSmileUser(user.email) && (
                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                    Staff
                  </span>
                )}
              </div>
              <button
                onClick={handleSignOut}
                className="text-gray-500 hover:text-gray-700 transition-colors"
                title="Sign Out"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-center shadow-lg"
               style={{ backgroundColor: 'rgba(254, 242, 242, 0.9)' }}>
            <p className="text-red-700">{error}</p>
            <button 
              onClick={loadReactions}
              className="mt-2 text-red-600 underline hover:text-red-800"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Main Reaction Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full shadow-sm mb-6 border"
               style={{ 
                 backgroundColor: 'rgba(255, 255, 255, 0.9)',
                 borderColor: 'rgba(255, 255, 255, 0.3)'
               }}>
            <Heart className="w-5 h-5 text-red-500" />
            <span className="text-gray-600 font-medium">Patient Feedback</span>
          </div>
          
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
            How was your <span style={{
              background: 'linear-gradient(135deg, #2563eb, #059669)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>experience</span>?
          </h2>
          <p className="text-gray-600 text-lg mb-8 max-w-2xl mx-auto">
            Your feedback helps us continuously improve our dental care services and ensures every patient has a pristine experience.
          </p>
        </div>

        {/* Reaction Buttons */}
        <div className="flex justify-center gap-8 mb-12">
          <button
            onClick={() => handleReaction('happy')}
            disabled={userReaction || isSubmitting}
            className={`group relative p-8 rounded-3xl transition-all duration-300 transform hover:scale-105 shadow-lg border cursor-pointer ${
              userReaction === 'happy'
                ? 'bg-green-500 shadow-2xl scale-105'
                : userReaction || isSubmitting
                ? 'cursor-not-allowed'
                : 'hover:shadow-xl'
            }`}
            style={{
              backgroundColor: userReaction === 'happy' 
                ? '#059669' 
                : userReaction || isSubmitting
                ? 'rgba(229, 231, 235, 0.8)'
                : 'rgba(255, 255, 255, 0.9)',
              borderColor: 'rgba(255, 255, 255, 0.3)'
            }}
          >
            <div className="flex flex-col items-center gap-4">
              {isSubmitting && pendingReactionType !== 'unhappy' ? (
                <Loader className="w-16 h-16 text-green-500 animate-spin" />
              ) : (
                <Smile 
                  className={`w-16 h-16 transition-colors duration-300 ${
                    userReaction === 'happy' ? 'text-white' : 'text-green-500'
                  }`} 
                />
              )}
              <span className={`text-xl font-semibold ${
                userReaction === 'happy' ? 'text-white' : 'text-gray-700'
              }`}>
                Great Experience
              </span>
            </div>
          </button>

          <button
            onClick={() => handleReaction('unhappy')}
            disabled={userReaction || isSubmitting}
            className={`group relative p-8 rounded-3xl transition-all duration-300 transform hover:scale-105 shadow-lg border cursor-pointer ${
              userReaction === 'unhappy'
                ? 'bg-blue-500 shadow-2xl scale-105'
                : userReaction || isSubmitting
                ? 'cursor-not-allowed'
                : 'hover:shadow-xl'
            }`}
            style={{
              backgroundColor: userReaction === 'unhappy' 
                ? '#3b82f6' 
                : userReaction || isSubmitting
                ? 'rgba(229, 231, 235, 0.8)'
                : 'rgba(255, 255, 255, 0.9)',
              borderColor: 'rgba(255, 255, 255, 0.3)'
            }}
          >
            <div className="flex flex-col items-center gap-4">
              {isSubmitting && pendingReactionType !== 'happy' ? (
                <Loader className="w-16 h-16 text-blue-500 animate-spin" />
              ) : (
                <Frown 
                  className={`w-16 h-16 transition-colors duration-300 ${
                    userReaction === 'unhappy' ? 'text-white' : 'text-blue-500'
                  }`} 
                />
              )}
              <span className={`text-xl font-semibold ${
                userReaction === 'unhappy' ? 'text-white' : 'text-gray-700'
              }`}>
                Needs Improvement
              </span>
            </div>
          </button>
        </div>

        {/* Thank You Message */}
        {showThankYou && (
          <div className="text-center mb-8" style={{ animation: 'fadeIn 0.5s ease-out' }}>
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full shadow-lg border"
                 style={{ 
                   backgroundColor: 'rgba(255, 255, 255, 0.95)',
                   borderColor: 'rgba(255, 255, 255, 0.3)'
                 }}>
              <Heart className="w-5 h-5 text-red-500" />
              <span className="text-gray-700 font-medium">
                Thank you for your valuable feedback! 
              </span>
            </div>
          </div>
        )}

        {/* Real-time Statistics */}
        <div className="grid md:grid-cols-3 gap-6">
          {/* Satisfaction Rate */}
          <div className="rounded-2xl p-6 shadow-lg border"
               style={{ 
                 backgroundColor: 'rgba(255, 255, 255, 0.9)',
                 borderColor: 'rgba(255, 255, 255, 0.3)'
               }}>
            <div className="flex items-center justify-between mb-4">
              <TrendingUp className="w-8 h-8 text-green-500" />
              <span className={`text-3xl font-bold text-green-600 ${animateNumbers ? 'animate-bounce' : ''}`}>
                {happyPercentage}%
              </span>
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Satisfaction Rate</h3>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="h-2 rounded-full transition-all duration-500"
                style={{ 
                  width: `${happyPercentage}%`,
                  background: 'linear-gradient(90deg, #22c55e, #16a34a)'
                }}
              ></div>
            </div>
          </div>

          {/* Total Feedback */}
          <div className="rounded-2xl p-6 shadow-lg border"
               style={{ 
                 backgroundColor: 'rgba(255, 255, 255, 0.9)',
                 borderColor: 'rgba(255, 255, 255, 0.3)'
               }}>
            <div className="flex items-center justify-between mb-4">
              <Users className="w-8 h-8 text-blue-500" />
              <span className={`text-3xl font-bold text-blue-600 ${animateNumbers ? 'animate-bounce' : ''}`}>
                {reactions.total}
              </span>
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Total Feedback</h3>
            <p className="text-gray-600">Patients shared their experience</p>
          </div>

          {/* Happy Patients */}
          <div className="rounded-2xl p-6 shadow-lg border"
               style={{ 
                 backgroundColor: 'rgba(255, 255, 255, 0.9)',
                 borderColor: 'rgba(255, 255, 255, 0.3)'
               }}>
            <div className="flex items-center justify-between mb-4">
              <MessageCircle className="w-8 h-8 text-green-500" />
              <span className={`text-3xl font-bold text-green-600 ${animateNumbers ? 'animate-bounce' : ''}`}>
                {reactions.happy}
              </span>
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Happy Patients</h3>
            <p className="text-gray-600">Positive experiences recorded</p>
          </div>
        </div>

        {/* Enhanced "Your Voice Matters" Section */}
        <div className="text-center mt-12">
          <div className="rounded-3xl p-8 shadow-2xl border max-w-2xl mx-auto relative overflow-hidden"
               style={{
                 background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 50%, rgba(239,246,255,0.8) 100%)',
                 borderColor: 'rgba(255, 255, 255, 0.4)'
               }}>
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-20 h-20 rounded-full -translate-y-10 translate-x-10"
                 style={{ background: 'linear-gradient(135deg, rgba(191, 219, 254, 0.2), rgba(187, 247, 208, 0.2))' }}></div>
            <div className="absolute bottom-0 left-0 w-16 h-16 rounded-full translate-y-8 -translate-x-8"
                 style={{ background: 'linear-gradient(45deg, rgba(187, 247, 208, 0.2), rgba(191, 219, 254, 0.2))' }}></div>
            
            <div className="relative z-10">
              <h3 className="text-2xl font-bold mb-6 tracking-wide"
                  style={{
                    background: 'linear-gradient(135deg, #1e40af, #2563eb, #059669)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }}>
                Your Voice Matters
              </h3>
              <p className="text-gray-700 leading-relaxed text-lg tracking-wide" style={{ fontWeight: '300' }}>
                At <span className="font-semibold text-blue-600">Pristine Smile</span>, every patient's feedback is invaluable to us. Whether it's praise or constructive criticism, 
                your input helps us maintain our high standards of dental care and continuously improve our services.
              </p>
              {userReaction === 'unhappy' && (
                <div className="mt-6 p-6 rounded-2xl border"
                     style={{
                       background: 'linear-gradient(135deg, rgba(239,246,255,0.8), rgba(219,234,254,0.6))',
                       borderColor: 'rgba(147, 197, 253, 0.3)'
                     }}>
                  <p className="text-blue-800 font-semibold mb-3 text-lg">We'd love to make this right!</p>
                  <p className="text-blue-700">
                    Please contact us at <a href="tel:+91 84017 49379" className="font-semibold underline hover:text-blue-800 transition-colors" style={{ textDecorationThickness: '2px', textUnderlineOffset: '2px' }}>+91 84017 49379</a> so we can address your concerns personally.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showAuthModal && <AuthModal />}
      {showFeedbackModal && <FeedbackModal />}
      {showReviewModal && <ReviewModal />}

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes float {
          0%, 100% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.05);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.95);
          }
        }
      `}</style>
    </div>
  );
};

export default PatientFeedback;